/**
 * Modo `embedded` — monta el iframe inline al 100% de ancho dentro del
 * contenedor y ajusta su altura a partir de los mensajes `finva:resize`.
 *
 * No hay scroll interno: el iframe crece para abarcar el contenido del
 * formulario. Una min-height inicial razonable evita el “salto” antes del
 * primer resize (ver §8.3 plan, §13 riesgo jitter).
 */
import type { Messenger } from '../transport/messenger';
import { emptyContainer } from '../util/dom';

const INITIAL_MIN_HEIGHT = 420; // px: reservado hasta el primer finva:resize.

export interface EmbeddedMount {
  /** `<iframe>` montado (null tras `unmount`). */
  readonly iframe: HTMLIFrameElement | null;
  /** Libera el iframe y limpia sus handlers. */
  unmount(): void;
}

export interface EmbeddedMountOptions {
  container: HTMLElement;
  iframe: HTMLIFrameElement;
  messenger: Messenger;
}

/** Monta el iframe en el contenedor y suscribe el auto-resize. */
export function mountEmbedded(opts: EmbeddedMountOptions): EmbeddedMount {
  const { container, iframe, messenger } = opts;

  emptyContainer(container);
  iframe.style.width = '100%';
  iframe.style.height = `${INITIAL_MIN_HEIGHT}px`;
  container.appendChild(iframe);

  let offResize: (() => void) | null = null;

  offResize = messenger.on('finva:resize', (payload) => {
    const height = (payload as { height?: number } | null)?.height;
    if (typeof height === 'number' && height > 0 && iframe.isConnected) {
      iframe.style.height = `${Math.ceil(height)}px`;
    }
  });

  return {
    get iframe(): HTMLIFrameElement | null {
      return iframe.isConnected ? iframe : null;
    },
    unmount(): void {
      if (offResize) {
        offResize();
        offResize = null;
      }
      emptyContainer(container);
    },
  };
}